<template>
  <form
    class="reimbursement-request-form"
    @submit.prevent="submit"
  >
    <alert-box
      v-for="alert in alerts"
      :key="alert.message"
      :type="alert.type"
    >
      {{ alert.message }}
    </alert-box>
    <div class="row">
      <div class="col-md-12">
        <table class="table table-striped">
          <thead>
            <tr>
              <th class="request-item-selected-to-return">
                <label>
                  <input
                    type="checkbox"
                    :checked="selectAll"
                    class="select-all"
                    @click="toggleSelectAll"
                  />
                </label>
              </th>
              <th class="request-item-name">Name of Item</th>
              <th class="request-item-quantity-purchased">Quantity Purchased</th>
              <th v-if="previousRequestsExist" class="request-item-quantity-already-requested">
                Quantity Previously Requested
              </th>
              <th class="request-item-quantity">Quantity to return</th>
              <th class="request-item-reason">
                <label>
                  Reason for Return
                  <select
                    v-if="selectAll"
                    v-model="returnReasonIdForAll"
                    class="form-input apply-all-select-box"
                  >
                    <option class="first">Select a reason for the return</option>
                    <option
                      v-for="returnReason in returnReasons"
                      :key="returnReason.id"
                      :value="returnReason.id"
                    >
                      {{ returnReason.name }}
                    </option>
                  </select>
                </label>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-if="preloading">
              <tr>
                <td :colspan="totalColumns">
                  <p class="center">
                    Finding your order...
                  </p>
                </td>
              </tr>
            </template>
            <template v-else>
              <template v-if="anyReturnableItems">
                <template v-for="requestItem in requestItems">
                  <form-row
                    v-if="requestItem.quantity_could_return > 0"
                    :key="requestItem.line_item_id"
                    :select-all="selectAll"
                    :name="requestItem.name"
                    :variant-id="requestItem.variant_id"
                    :line-item-id="requestItem.line_item_id"
                    :quantity-purchased="requestItem.quantity_purchased"
                    :quantity-already-requested="requestItem.quantity_already_requested"
                    :quantity-could-return="requestItem.quantity_could_return"
                    :return-reasons="returnReasons"
                    :return-reason-id-for-all="returnReasonIdForAll"
                    :previous-requests-exist="previousRequestsExist"
                    @add-item="addItem"
                    @remove-item="removeItem"
                  />
                  <tr v-else :key="requestItem.line_item_id" class="request-item-row">
                    <td />
                    <td :colspan="totalColumns">
                      {{ requestItem.name }}
                      cannot be selected as it already has all
                      {{ requestItem.quantityAlreadyRequested }}
                      items previously requested.
                    </td>
                  </tr>
                </template>
                <tr
                  v-for="freeSample in freeSamples"
                  :key="freeSample.name"
                  class="request-item-row free-sample"
                >
                  <td class="request-item-selected-to-return">
                    <input type="checkbox" disabled />
                  </td>
                  <td class="request-item-name">
                    {{ freeSample.name }}
                  </td>
                  <td class="request-item-quantity-purchased">
                    {{ freeSample.quantity }}
                  </td>
                  <td :colspan="totalColumns - 3">
                    Free samples are not eligible for returns or refunds.
                  </td>
                </tr>
              </template>
              <template v-else>
                <tr>
                  <td :colspan="totalColumns">
                    <p class="center">
                      There are no items available to return.
                    </p>
                  </td>
                </tr>
              </template>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <div class="row">
      <template v-if="anyReturnableItems">
        <div class="col-sm-2 col-sm-offset-4">
          <a :href="cancelPath" class="btn btn-light-orange reimbursement-request-btn">
            Cancel
          </a>
        </div>
        <div class="col-sm-2">
          <input
            type="submit"
            :value="submitLoading ? 'Please Wait' : 'Submit'"
            :disabled="submitLoading"
            class="btn btn-light-green reimbursement-request-btn"
          />
        </div>
      </template>
      <template v-else>
        <div class="col-sm-12">
          <a :href="cancelPath" class="btn btn-light-orange reimbursement-request-btn">
            Cancel
          </a>
        </div>
      </template>
    </div>
  </form>
</template>

<script>
  import axios from 'axios'
  import alerts from 'mixins/storefront/cart-alerts'
  import FormRow from './reimbursement-request/form-row.vue';

  export default {
    components: {
      FormRow
    },
    mixins: [alerts],
    props: {
      newPath: { type: String, required: true },
      createPath: { type: String, required: true },
      cancelPath: { type: String, required: true },
    },
    data() {
      return {
        preloading: true,
        submitLoading: false,
        reimbursementRequest: {},
        selectAll: false,
        returnReasonIdForAll: null,
        requestItems: [],
        freeSamples: [],
        shipmentNumber: '',
        returnReasons: []
      }
    },
    computed: {
      previousRequestsExist() {
        return this.requestItems.some((requestItem) => {
          return requestItem.quantity_already_requested > 0;
        });
      },
      anyReturnableItems() {
        return this.requestItems.length > 0;
      },
      totalColumns() {
        const baseColumns = 5;
        return this.previousRequestsExist ? baseColumns + 1 : baseColumns;
      },
      submittableRequestItems() {
        const items = [];
        Object.entries(this.reimbursementRequest).forEach(([, payload]) => {
          items.push(payload);
        });
        return items;
      }
    },
    created() {
      axios.get(this.newPath)
        .then((response) => {
          switch (response.status) {
            case 200:
              this.shipmentNumber = response.data.shipment_number;
              this.requestItems = response.data.request_items;
              this.freeSamples = response.data.free_samples;
              this.returnReasons = response.data.possible_return_reasons;
              this.preloading = false;
              break;
            case 422:
              response.data.errors.forEach((error) => {
                this.alert(error, 'danger');
              });
              break;
            default:
              break;
          }
        })
        .catch((error) => {
          this.alert(error, 'danger');
        });
    },
    methods: {
      toggleSelectAll() {
        this.selectAll = !this.selectAll;
      },
      addItem(payload) {
        this.$set(
          this.reimbursementRequest,
          payload.line_item_id.toString(),
          payload
        )
      },
      removeItem(lineItemId) {
        this.$delete(this.reimbursementRequest, lineItemId.toString());
      },
      submit() {
        this.alerts = [];

        if (Object.entries(this.reimbursementRequest).length === 0) {
          this.alert('You must include at least one item');
          return
        }

        this.submitLoading = true;
        axios.post(this.createPath, {
          shipment_number: this.shipmentNumber,
          reimbursement_request: {
            request_items_attributes: this.submittableRequestItems
          }
        }).then((response) => {
          switch (response.status) {
            case 200:
              window.location.assign(response.data.show_path);
              break;
            case 422:
              response.data.errors.forEach((error) => {
                this.alert(error, 'danger');
              });
              this.submitLoading = false;
              break;
            default:
              break;
          }
        }).catch((error) => {
          this.alert(error, 'danger');
          this.submitLoading = false;
        });
      },
    },
  };
</script>
